import { isEmail } from "validator";

import { getCookieByName } from "./cookie";
import { canAccessClientStorageWrapper } from "./embed";
import { getFromLocalStorage, removeFromLocalStorage, setInLocalStorage } from "./localStorage";

export const REPEAT_DONOR_EMAIL_LOCAL_STORAGE_KEY = "repeatDonorEmail" as const;
const REPEAT_DONOR_TOKEN_LOCAL_STORAGE_KEY = "repeatDonorToken" as const;
const REPEAT_DONOR_EMAIL_COOKIE_NAME = "zeffy_repeat_donor_email";
const REPEAT_DONOR_TOKEN_COOKIE_NAME = "zeffy_repeat_donor_token";
const REPEAT_DONOR_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

// Normalized in one place so every channel (localStorage, cookie, server cookie) stores the same key.
export const normalizeRepeatDonorEmail = (email: string | null | undefined): string | null => {
  if (!email) {
    return null;
  }

  const normalizedEmail = email.trim().toLowerCase();

  return isEmail(normalizedEmail) ? normalizedEmail : null;
};

export const storeRepeatDonorEmail = (email: string | null | undefined): void => {
  const normalizedEmail = normalizeRepeatDonorEmail(email);

  if (!normalizedEmail) {
    return;
  }

  setInLocalStorage?.(REPEAT_DONOR_EMAIL_LOCAL_STORAGE_KEY, normalizedEmail);
  canAccessClientStorageWrapper(() => {
    document.cookie = `${REPEAT_DONOR_EMAIL_COOKIE_NAME}=${encodeURIComponent(normalizedEmail)}; max-age=${REPEAT_DONOR_COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax; Secure`;
  });
};

export const storeRepeatDonorToken = (token: string): void => {
  setInLocalStorage?.(REPEAT_DONOR_TOKEN_LOCAL_STORAGE_KEY, token);
  canAccessClientStorageWrapper(() => {
    document.cookie = `${REPEAT_DONOR_TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}; max-age=${REPEAT_DONOR_COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax; Secure`;
  });
};

export const clearRepeatDonorToken = (): void => {
  removeFromLocalStorage?.(REPEAT_DONOR_TOKEN_LOCAL_STORAGE_KEY);
  canAccessClientStorageWrapper(() => {
    document.cookie = `${REPEAT_DONOR_TOKEN_COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax; Secure`;
  });
};

export const getStoredRepeatDonorToken = (): string | null => {
  const tokenFromLocalStorage = getFromLocalStorage?.<string>(REPEAT_DONOR_TOKEN_LOCAL_STORAGE_KEY);
  const rawTokenFromCookie = canAccessClientStorageWrapper(() => getCookieByName(REPEAT_DONOR_TOKEN_COOKIE_NAME));
  const tokenFromCookie = rawTokenFromCookie ? decodeURIComponent(rawTokenFromCookie) : undefined;

  return (typeof tokenFromLocalStorage === "string" ? tokenFromLocalStorage : undefined) || tokenFromCookie || null;
};

export const getStoredRepeatDonorEmail = (): string | null => {
  const emailFromLocalStorage = getFromLocalStorage?.<string>(REPEAT_DONOR_EMAIL_LOCAL_STORAGE_KEY);
  const rawEmailFromCookie = canAccessClientStorageWrapper(() => getCookieByName(REPEAT_DONOR_EMAIL_COOKIE_NAME));
  const emailFromCookie = rawEmailFromCookie ? decodeURIComponent(rawEmailFromCookie) : undefined;

  const email =
    (typeof emailFromLocalStorage === "string" ? emailFromLocalStorage : undefined) || emailFromCookie || null;

  return email && isEmail(email) ? email : null;
};
